import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Check, ArrowRight, FlaskConical, UserCheck, Ban } from 'lucide-react';

// Minimum age for the verification gate (legal requirement: 18).
const MIN_AGE = 18;

const CHECKS = [
  {
    id: 'age',
    icon: UserCheck,
    label: `I confirm that I am ${MIN_AGE} years of age or older.`,
  },
  {
    id: 'researcher',
    icon: FlaskConical,
    label: 'I am a qualified researcher or am purchasing for legitimate laboratory research purposes.',
  },
  {
    id: 'research_only',
    icon: Ban,
    label: 'I understand all products are strictly for laboratory and research use only — NOT for human or animal consumption.',
  },
];

/**
 * Two-step first-visit gate for Genora.
 *  Step 1 ("verify")  → three required consent checkboxes.
 *  Step 2 ("welcome") → Sign In / Create Account / Continue as Guest.
 *
 * Props:
 *  - onComplete(): persist verification + dismiss the gate (called the moment
 *    the visitor passes step 2 via any of sign-in / create / guest).
 *  - onExit(): visitor does not meet the requirements (redirect away).
 */
export const FirstVisitGate = ({ onComplete, onExit }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState('verify');
  const [checked, setChecked] = useState({});

  const allChecked = CHECKS.every((c) => checked[c.id]);
  const toggle = (id) => setChecked((prev) => ({ ...prev, [id]: !prev[id] }));

  const goAuth = (register) => {
    onComplete();
    navigate(register ? '/auth?mode=register' : '/auth');
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-md">
      {/* soft brand glow */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(620px circle at 50% -5%, rgba(16,185,129,0.16), transparent 60%)' }}
      />

      <div
        className="relative w-full max-w-md rounded-3xl border border-emerald-500/20 bg-slate-900/95 p-8 shadow-2xl animate-in fade-in zoom-in-95 duration-300"
        data-testid="first-visit-gate"
      >
        {/* Brand mark */}
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/10 ring-1 ring-emerald-500/30">
            <ShieldCheck className="h-8 w-8 text-emerald-400" />
          </div>
        </div>

        {step === 'verify' ? (
          <div data-testid="gate-step-verify">
            <h2 className="text-center text-2xl font-bold text-white">Verification Required</h2>
            <p className="mt-2 text-center text-sm text-slate-400">
              Please confirm the following before entering Genora.
            </p>

            <div className="mt-6 space-y-3">
              {CHECKS.map((c) => {
                const Icon = c.icon;
                const on = !!checked[c.id];
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggle(c.id)}
                    aria-pressed={on}
                    className={`flex w-full items-start gap-3 rounded-xl border p-3 text-left transition-colors ${
                      on
                        ? 'border-emerald-500/50 bg-emerald-500/10'
                        : 'border-slate-700 bg-slate-800/40 hover:border-slate-600'
                    }`}
                    data-testid={`gate-check-${c.id}`}
                  >
                    <span
                      className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border transition-colors ${
                        on ? 'border-emerald-400 bg-emerald-500' : 'border-slate-500 bg-transparent'
                      }`}
                    >
                      {on && <Check className="h-3.5 w-3.5 text-slate-950" strokeWidth={3} />}
                    </span>
                    <span className="flex items-start gap-2 text-sm leading-snug text-slate-300">
                      <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-400/80" />
                      {c.label}
                    </span>
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              disabled={!allChecked}
              onClick={() => setStep('welcome')}
              className={`mt-6 flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3 font-semibold transition-all ${
                allChecked
                  ? 'bg-emerald-500 text-slate-950 hover:bg-emerald-400 shadow-lg shadow-emerald-500/20'
                  : 'cursor-not-allowed bg-slate-800 text-slate-500'
              }`}
              data-testid="gate-continue-btn"
            >
              Continue
              <ArrowRight className="h-4 w-4" />
            </button>

            <button
              type="button"
              onClick={onExit}
              className="mt-3 block w-full text-center text-xs text-slate-500 transition-colors hover:text-slate-400"
              data-testid="gate-exit-btn"
            >
              I do not meet these requirements — Exit
            </button>
          </div>
        ) : (
          <div data-testid="gate-step-welcome">
            <h2 className="text-center text-2xl font-bold text-white">Welcome to Genora</h2>
            <p className="mt-2 text-center text-sm text-slate-400">
              Sign in or create an account to explore our research-grade catalog, track orders, and unlock member pricing.
            </p>

            <div className="mt-6 space-y-3">
              <button
                type="button"
                onClick={() => goAuth(false)}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 px-6 py-3 font-semibold text-slate-950 shadow-lg shadow-emerald-500/20 transition-all hover:bg-emerald-400"
                data-testid="gate-signin-btn"
              >
                Sign In
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => goAuth(true)}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-500/40 bg-transparent px-6 py-3 font-semibold text-emerald-300 transition-colors hover:bg-emerald-500/10"
                data-testid="gate-create-btn"
              >
                Create Account
              </button>
            </div>

            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-700" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-slate-900 px-3 text-xs uppercase tracking-wide text-slate-500">or</span>
              </div>
            </div>

            <button
              type="button"
              onClick={onComplete}
              className="block w-full text-center text-sm text-slate-400 underline-offset-4 transition-colors hover:text-slate-200 hover:underline"
              data-testid="gate-guest-btn"
            >
              Continue as Guest
            </button>

            <p className="mt-5 text-center text-[11px] leading-relaxed text-slate-500">
              By creating an account you agree to our research disclaimer and to receive our newsletter. You can unsubscribe anytime.
            </p>
          </div>
        )}

        <p className="mt-6 text-center text-[11px] tracking-wide text-slate-600">
          GENORA LLC &bull; RESEARCH-GRADE COMPOUNDS
        </p>
      </div>
    </div>
  );
};

export default FirstVisitGate;
